import { useEffect, useState } from "react";
import { Plus, Pencil, Save, ToggleLeft, ToggleRight, X } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatAdminError, absolutePhotoUrl } from "@/lib/adminApi";
import {
  EmptyState,
  GhostButton,
  PageHeader,
  Panel,
  PrimaryButton,
} from "@/components/admin/uiKit";

const EMPTY = {
  slug: "",
  title: "",
  body: "",
  icon: "leaf",
  image_url: "",
  order: 99,
  active: true,
};

const ICON_OPTIONS = ["leaf", "shield", "calendar", "camera", "sparkles", "truck"];

export default function AdminValueProps() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => {
    adminApi
      .get("/value-props")
      .then(({ data }) => setItems(data.items || []))
      .catch((err) => toast.error(formatAdminError(err)));
  };

  useEffect(load, []);

  const toggle = async (item) => {
    try {
      if (item.active) {
        await adminApi.delete(`/admin/value-props/${item.id}`);
      } else {
        await adminApi.patch(`/admin/value-props/${item.id}`, { active: true });
      }
      toast.success(item.active ? "Hidden" : "Live");
      load();
    } catch (err) {
      toast.error(formatAdminError(err));
    }
  };

  return (
    <div data-testid="admin-value-props">
      <PageHeader
        overline="Content"
        title="Value Props"
        description="The four 'Why Silky Smooth' cards on the home page. Edit copy, swap photos, reorder."
        actions={
          <PrimaryButton onClick={() => setEditing({ ...EMPTY })} data-testid="admin-value-props-new">
            <Plus size={14} /> New value prop
          </PrimaryButton>
        }
      />

      {items === null ? (
        <Panel className="p-10 text-center">
          <div className="h-6 w-6 mx-auto rounded-full border-2 border-sky-200 border-t-sky-500 animate-spin" />
        </Panel>
      ) : items.length === 0 ? (
        <EmptyState
          title="No value props yet"
          description="Add at least 3 to populate the homepage band."
          action={
            <PrimaryButton onClick={() => setEditing({ ...EMPTY })}>
              <Plus size={14} /> Add the first
            </PrimaryButton>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((v) => (
            <article
              key={v.id}
              className="rounded-xl bg-white border border-slate-200 overflow-hidden flex flex-col md:flex-row shadow-[0_2px_8px_-4px_rgba(15,23,42,0.06)]"
              data-testid={`admin-value-prop-card-${v.id}`}
            >
              <div className="md:w-44 aspect-[4/3] md:aspect-auto bg-slate-100 relative">
                {v.image_url ? (
                  <img
                    src={absolutePhotoUrl(v.image_url)}
                    alt={v.title}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                ) : null}
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] bg-white/95 text-slate-700 px-2 py-1 rounded-md">
                  #{v.order ?? "—"}
                </span>
              </div>
              <div className="flex-1 p-5">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-bold text-slate-900">{v.title}</p>
                  <button
                    type="button"
                    onClick={() => toggle(v)}
                    className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-1 rounded-md ${
                      v.active
                        ? "bg-emerald-500 text-white"
                        : "bg-slate-200 text-slate-600"
                    }`}
                    data-testid={`admin-value-prop-toggle-${v.id}`}
                  >
                    {v.active ? <ToggleRight size={12} /> : <ToggleLeft size={12} />}
                    {v.active ? "Live" : "Hidden"}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mb-2 uppercase tracking-[0.18em]">{v.icon}</p>
                <p className="text-sm text-slate-700 leading-relaxed">{v.body}</p>
                <div className="mt-4 flex items-center gap-2">
                  <GhostButton onClick={() => setEditing(v)} data-testid={`admin-value-prop-edit-${v.id}`}>
                    <Pencil size={12} /> Edit
                  </GhostButton>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {editing && (
        <ValuePropForm
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function ValuePropForm({ item, onClose, onSaved }) {
  const isNew = !item.id;
  const [form, setForm] = useState({ ...EMPTY, ...item });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.slug.trim() || !form.title.trim() || !form.body.trim()) {
      toast.error("Slug, title, and body are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        slug: form.slug.trim(),
        title: form.title.trim(),
        body: form.body.trim(),
        icon: form.icon,
        image_url: form.image_url.trim(),
        order: parseInt(form.order, 10),
        active: !!form.active,
      };
      if (isNew) {
        await adminApi.post("/admin/value-props", payload);
        toast.success("Value prop added");
      } else {
        const { slug, ...rest } = payload;
        await adminApi.patch(`/admin/value-props/${item.id}`, rest);
        toast.success("Saved");
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatAdminError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" data-testid="admin-value-prop-form">
      <button
        type="button"
        onClick={onClose}
        aria-label="Dismiss"
        className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm"
      />
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[520px] bg-white shadow-2xl overflow-y-auto">
        <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-bold">
            {isNew ? "New value prop" : "Edit value prop"}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="h-9 w-9 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 grid place-items-center"
          >
            <X size={16} />
          </button>
        </header>

        <div className="p-6 space-y-4">
          <FormRow label="Slug">
            <input
              className="form-input"
              value={form.slug}
              onChange={(e) => update("slug", e.target.value)}
              disabled={!isNew}
              data-testid="admin-vp-input-slug"
            />
          </FormRow>
          <FormRow label="Title">
            <input
              className="form-input"
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              data-testid="admin-vp-input-title"
            />
          </FormRow>
          <FormRow label="Body">
            <textarea
              rows={4}
              className="form-input"
              value={form.body}
              onChange={(e) => update("body", e.target.value)}
              data-testid="admin-vp-input-body"
            />
          </FormRow>
          <div className="grid grid-cols-2 gap-3">
            <FormRow label="Icon">
              <select
                className="form-input"
                value={form.icon}
                onChange={(e) => update("icon", e.target.value)}
                data-testid="admin-vp-input-icon"
              >
                {ICON_OPTIONS.map((i) => (
                  <option key={i} value={i}>{i}</option>
                ))}
              </select>
            </FormRow>
            <FormRow label="Order">
              <input
                type="number"
                className="form-input"
                value={form.order}
                onChange={(e) => update("order", e.target.value)}
              />
            </FormRow>
          </div>
          <FormRow label="Image URL">
            <input
              className="form-input"
              value={form.image_url}
              onChange={(e) => update("image_url", e.target.value)}
              data-testid="admin-vp-input-image"
            />
          </FormRow>
          <FormRow label="Active">
            <label className="form-input flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.active}
                onChange={(e) => update("active", e.target.checked)}
              />
              <span className="text-sm text-slate-700">Show on site</span>
            </label>
          </FormRow>
          {form.image_url && (
            <div className="rounded-lg overflow-hidden border border-slate-200 aspect-[4/3] bg-slate-100">
              <img
                src={absolutePhotoUrl(form.image_url)}
                alt="preview"
                className="h-full w-full object-cover"
              />
            </div>
          )}
        </div>

        <footer className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex items-center gap-2">
          <PrimaryButton onClick={save} disabled={saving} data-testid="admin-vp-save">
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </PrimaryButton>
          <GhostButton onClick={onClose}>Cancel</GhostButton>
        </footer>

        <style>{`
          .form-input {
            width: 100%;
            min-height: 2.75rem;
            border-radius: 0.5rem;
            border: 1px solid #E2E8F0;
            background: #fff;
            padding: 0.5rem 0.75rem;
            font-size: 0.875rem;
            color: #0F172A;
          }
          .form-input:focus {
            outline: none;
            border-color: #38BDF8;
            box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
          }
        `}</style>
      </aside>
    </div>
  );
}

function FormRow({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
